import React, { useEffect, useRef, useState } from 'react';
import { FaBell } from 'react-icons/fa';
import axios from 'axios';
import { io } from 'socket.io-client';
import notificationSound from './notification.wav'; // Ensure this file exists in the same folder
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function OrderNotification() {
  const audioRef = useRef(null);
  const dropdownRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hovered, setHovered] = useState(false);

  // Close dropdown if user clicks outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Fetch initial activities to populate notification list on mount
    const fetchInitialNotifications = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        const res = await axios.get(`${API_BASE_URL}/admin/activities`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { page: 1, limit: 5, type: 'all' }
        });
        const fetchedActs = res.data?.activities || [];
        const initialNotifs = fetchedActs.map(act => ({
          id: act.orderId || act._id,
          title: act.title,
          message: act.message,
          link: `/admin/orders-details/${act.orderId}`,
          time: act.createdAt,
          unread: true
        }));
        setNotifications(initialNotifs);
      } catch (err) {
        // Ignore if unauthenticated or error
      }
    };
    fetchInitialNotifications();

    // Connect to WebSocket server
    const socket = io(API_BASE_URL);

    // Listen for new activity events to update dropdown and trigger audio-visual alerts
    socket.on('activity-added', (activity) => {
      console.log('[Socket.io] Dropdown received new activity:', activity);

      const newNotif = {
        id: activity.orderId,
        title: activity.title,
        message: activity.message,
        link: `/admin/orders-details/${activity.orderId}`,
        time: activity.createdAt || new Date(),
        unread: true
      };

      setNotifications(prev => [newNotif, ...prev.slice(0, 9)]);

      // 1. Play chime sound
      if (audioRef.current) {
        audioRef.current.play().catch(e => {
          console.log('[Autoplay] Audio play blocked:', e);
        });
      }

      // 2. Trigger appropriate Toast style depending on activity type
      const toastBody = ({ closeToast }) => (
        <div style={{ padding: '2px' }}>
          <div style={{ fontWeight: 700, marginBottom: '4px', fontSize: '13px' }}>{activity.title}</div>
          <div style={{ fontSize: '12px', color: '#334155' }}>
            {activity.message}
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
            <button
              onClick={closeToast}
              style={{ background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: '10px', fontWeight: 600 }}
            >Dismiss</button>
            <Link
              to={`/admin/orders-details/${activity.orderId}`}
              style={{ background: activity.type === 'payment_received' ? '#2e7d32' : activity.type === 'order_cancelled' ? '#c62828' : '#1a237e', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', textDecoration: 'none', fontWeight: 600, display: 'inline-block', fontSize: '10px' }}
              onClick={closeToast}
            >View Details</Link>
          </div>
        </div>
      );

      const toastOptions = {
        position: "top-right",
        autoClose: 5000,
        closeOnClick: false,
        draggable: false,
        theme: "light"
      };

      if (activity.type === 'payment_received') {
        toast.success(toastBody, toastOptions);
      } else if (activity.type === 'order_cancelled') {
        toast.error(toastBody, toastOptions);
      } else {
        toast.info(toastBody, toastOptions);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleToggleDropdown = () => {
    setShowDropdown(prev => !prev);
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
  };

  const handleNotificationClick = (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, unread: false } : n));
    setShowDropdown(false);
  };

  const unreadCount = notifications.filter(n => n.unread).length;

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      {/* Bell Button */}
      <button
        title="Notifications"
        onClick={handleToggleDropdown}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          backgroundColor: hovered ? '#ffffff' : '#FFFF01',
          color: '#1a237e',
          border: 'none',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          cursor: 'pointer',
          position: 'relative',
          boxShadow: hovered ? '0 4px 12px rgba(255, 255, 1, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.2s ease-in-out'
        }}
      >
        <FaBell />
        {unreadCount > 0 && (
          <div style={{
            position: 'absolute',
            top: '-5px',
            right: '-5px',
            backgroundColor: '#ffffff',
            color: '#d32f2f',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            fontWeight: 'bold',
            padding: '0',
            lineHeight: '1',
            border: '2px solid #ffffff',
            boxShadow: '0 2px 5px rgba(0,0,0,0.15)'
          }}>
            {unreadCount}
          </div>
        )}
      </button>

      {/* Notification Dropdown Drawer */}
      {showDropdown && (
        <div style={{
          position: 'absolute',
          top: 45,
          right: 0,
          zIndex: 99999,
          background: '#ffffff',
          color: '#334155',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          width: '380px',
          fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #f1f5f9',
            background: '#f8fafc'
          }}>
            <span style={{ fontWeight: 700, color: '#1a237e', fontSize: '13.5px' }}>Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1a237e',
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Body List */}
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '12.5px' }}>
                No recent notifications
              </div>
            ) : (
              notifications.map((notif) => (
                <Link
                  key={notif.id}
                  to={notif.link}
                  onClick={() => handleNotificationClick(notif.id)}
                  style={{
                    display: 'block',
                    padding: '12px 16px',
                    borderBottom: '1px solid #f1f5f9',
                    textDecoration: 'none',
                    background: notif.unread ? 'rgba(26, 35, 126, 0.04)' : '#ffffff',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseOver={e => { e.currentTarget.style.backgroundColor = 'rgba(26, 35, 126, 0.08)'; }}
                  onMouseOut={e => { e.currentTarget.style.backgroundColor = notif.unread ? 'rgba(26, 35, 126, 0.04)' : '#ffffff'; }}
                >
                  <div style={{ display: 'flex', justifySpaceBetween: 'space-between', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '3px' }}>
                    <span style={{ fontWeight: 700, color: '#1a237e', fontSize: '12.5px' }}>{notif.title}</span>
                    <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                      {notif.time ? new Date(notif.time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : ''}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#475569', lineHeight: 1.4 }}>{notif.message}</div>
                </Link>
              ))
            )}
          </div>

          {/* Footer */}
          <div style={{
            borderTop: '1px solid #f1f5f9',
            background: '#f8fafc',
            textAlign: 'center',
            padding: '10px 16px'
          }}>
            <Link 
              to="/admin/notifications" 
              onClick={() => setShowDropdown(false)}
              style={{
                color: '#1a237e',
                textDecoration: 'none',
                fontSize: '12px',
                fontWeight: 600,
                display: 'inline-block',
                transition: 'opacity 0.15s'
              }}
              onMouseOver={e => { e.currentTarget.style.opacity = '0.85'; }}
              onMouseOut={e => { e.currentTarget.style.opacity = '1'; }}
            >
              View All Notifications
            </Link>
          </div>
        </div>
      )}

      {/* Hidden audio element for notification */}
      <audio ref={audioRef} src={notificationSound} preload="auto" />
    </div>
  );
}

export default OrderNotification;